<template>
  <div>
    <AppModal v-model="showAddRole" title="Add Role" size="xxl" hide-footer>
      <CreateRole :role-id="role_uid" @finished="closeDialog" />
    </AppModal>

    <!-- Header -->
    <div class="flex items-center justify-between py-3">
      <p class="text-sm text-gray-500">{{ roles.length }} role{{ roles.length !== 1 ? "s" : "" }} configured</p>
      <button class="btn-primary" @click="openForm">
        <i class="fa fa-plus mr-1"></i> Add Role
      </button>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th style="width: 2rem"></th>
          <th>Role Name</th>
          <th>Description</th>
          <th>Permissions</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <!-- Skeleton -->
        <template v-if="loading">
          <tr v-for="i in 5" :key="`sk-${i}`" class="animate-pulse">
            <td>
              <div class="h-3 bg-gray-200 rounded w-4"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-32"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-48"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-16"></div>
            </td>
            <td>
              <div class="h-3 bg-gray-200 rounded w-8"></div>
            </td>
          </tr>
        </template>

        <template v-else v-for="(role, idx) in roles" :key="idx">
          <!-- Main row -->
          <tr @click="toggleRow(idx)" class="cursor-pointer select-none">
            <td>
              <i class="fa text-gray-400 text-xs transition-transform duration-200"
                 :class="expandedRows.has(idx) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </td>
            <td class="font-semibold">{{ role.name }}</td>
            <td class="text-gray-600">{{ role.description || "—" }}</td>
            <td>
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                {{
                                role.permissions?.length ?? 0
                              }} permission{{ (role.permissions?.length ?? 0) !== 1 ? "s" : "" }}
                            </span>
            </td>
            <td @click.stop>
              <button class="btn-sm-primary" @click="editRole(role)">
                <i class="fa fa-edit mr-1"></i> Edit
              </button>
            </td>
          </tr>

          <!-- Expanded permissions row -->
          <tr v-if="expandedRows.has(idx)">
            <td colspan="5" class="bg-gray-50 px-4 py-3">
              <div v-if="role.permissions?.length" class="flex flex-wrap gap-2">
                                <span
                                  v-for="(perm, pidx) in role.permissions"
                                  :key="pidx"
                                  class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-white border border-primary/20 text-primary-700"
                                >
                                    {{ perm.permission }}
                                </span>
              </div>
              <p v-else class="text-xs text-gray-400 italic">No permissions assigned to this role.</p>
            </td>
          </tr>
        </template>

        <tr v-if="!loading && roles.length === 0">
          <td colspan="5" class="text-center py-6 text-gray-400 text-sm">No roles found</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1" class="flex items-center justify-between mt-4 px-1">
      <button
        class="btn-sm-primary"
        :disabled="currentPage <= 1"
        :class="currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
        @click="previous"
      >
        <i class="fa fa-chevron-left mr-1"></i> Previous
      </button>
      <span class="text-xs text-gray-500">
                Page {{ currentPage }} of {{ pagination.totalPages }}
            </span>
      <button
        class="btn-sm-primary"
        :disabled="currentPage >= pagination.totalPages"
        :class="currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
        @click="next"
      >
        Next <i class="fa fa-chevron-right ml-1"></i>
      </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import CreateRole from "@/domains/home/services/accounts/components/roles/CreateRole.vue";
import { useRolesStore } from "@/stores/roles";

interface Permission {
    permission: string
}

interface Role {
    roleId: string
    role_uid: string
    name: string
    description: string
    permissions: Permission[]
}

interface Pagination {
    totalElements: number
    totalPages: number
}

const rolesStore = useRolesStore();

const showAddRole = ref(false)
const loading = ref(false)
const currentPage = ref(1);
const roles = ref<Role[]>([])
const expandedRows = ref<Set<number>>(new Set());
const role_uid = ref<string | null>(null)
const pagination = ref<Pagination>({ totalElements: 0, totalPages: 0 });

onMounted(() => {
    fetch()
})

const fetch = () => {
    loading.value = true
  rolesStore.fetchRoles();
}

const toggleRow = (idx: number) => {
  if (expandedRows.value.has(idx)) {
    expandedRows.value.delete(idx);
  } else {
    expandedRows.value.add(idx);
  }
}

const openForm = () => {
    role_uid.value = null
    showAddRole.value = true
}

const editRole = (item: Role) => {
  rolesStore.fetchRoleDetails(item.roleId);
    role_uid.value = item.role_uid
    showAddRole.value = true
}

const closeDialog = () => {
    fetch()
    showAddRole.value = false
}

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
}

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  }
}

watch(() => rolesStore.rolesList, (data) => {
    loading.value = false
  if (data?.data) {
    roles.value = Array.isArray(data.data) ? data.data : (data.data.content ?? []);
  } else if (Array.isArray(data)) {
    roles.value = data;
  }
})
</script>
